// This file will be deprecated. API logic will move to apiMethods.js, endpoints.js, and per-feature files.
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include JWT token if present
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// API service functions
export const apiService = {
  // Health check
  checkHealth: async () => {
    try {
      const response = await api.get("/health");
      return response.data;
    } catch (error) {
      throw new Error("Backend server is not responding");
    }
  },

  // Authentication API
  login: async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      // Save token to localStorage
      if (response.data.token) {
        localStorage.setItem("jwtToken", response.data.token);
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Login failed");
    }
  },

  verifyToken: async (token) => {
    try {
      const response = await api.post("/auth/verify", { token });
      return response.data;
    } catch (error) {
      throw new Error("Token verification failed");
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get("/auth/me");
      return response.data;
    } catch (error) {
      throw new Error("Failed to get user info");
    }
  },

  logout: async () => {
    try {
      // Remove token from localStorage
      localStorage.removeItem("jwtToken");
      // Optionally call backend logout endpoint
      // await api.post("/auth/logout");
      return { success: true };
    } catch (error) {
      throw new Error("Logout failed");
    }
  },

  resetPassword: async (oldPassword, newPassword) => {
    try {
      const response = await api.post("/auth/reset-password", {
        oldPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to reset password"
      );
    }
  },

  // Users API
  getUsers: async () => {
    try {
      const response = await api.get("/users");
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch users");
    }
  },

  getUserById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch user");
    }
  },

  createUser: async (userData) => {
    try {
      const response = await api.post("/users", userData);
      return response.data;
    } catch (error) {
      throw new Error("Failed to create user");
    }
  },

  updateUser: async (id, userData) => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      throw new Error("Failed to update user");
    }
  },

  deleteUser: async (id) => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to delete user");
    }
  },

  // Prompts API
  getPrompts: async (params) => {
    try {
      const response = await api.get("/prompts", { params });
      return response.data;
    } catch (error) {
      console.error("API Service: Failed to fetch prompts:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch prompts"
      );
    }
  },

  submitPromptData: async (promptData) => {
    try {
      const response = await api.post("/prompts", promptData);
      return response.data;
    } catch (error) {
      console.error("API Service: Failed to submit prompt:", error);
      throw new Error(
        error.response?.data?.message || "Failed to submit prompt"
      );
    }
  },
};

export default api;
